import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  SoftwareManagementEditApplicationPageQueryDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import DropZone from 'modules/core/components/DropZone'
import SelectField from 'modules/core/components/SelectField'
import Sidepanel from 'modules/core/components/Sidepanel'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileList from 'modules/documents/components/FileList'
import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import ContractItem from 'modules/software_management/components/ApplicationDetailsContent/DetailsSidebar/_ContractItem'
import { useUpdateSoftwareSetup } from 'modules/software_management/hooks/useUpdateSoftwareSetup'

import { licenseTypeOptions } from 'modules/software_management/lib/software'

interface FormValues {
  renewalDate?: string
  licenseType?: string
  currentFile?: File
  vendor?: ContactFragment
}

interface Props {
  onClose: () => void
  softwareSetupId: number
  companyId: number
}

export const EditApplicationModal = ({ onClose, softwareSetupId, companyId }: Props) => {
  const { data, loading } = useQuery(SoftwareManagementEditApplicationPageQueryDocument, {
    variables: {
      companyId: companyId,
      softwareSetupId: softwareSetupId,
    },
  })

  const softwareSetup = data?.softwareManagement.softwareSetup

  const { updateSoftwareSetup, loading: updating } = useUpdateSoftwareSetup()

  const form = useForm<FormValues>({
    defaultValues: {
      renewalDate: softwareSetup?.renewalDate || '',
      licenseType: softwareSetup?.licenseType || '',
      currentFile: undefined,
    },
    onSubmit: async (formData) => {
      if (!softwareSetup) {
        return { success: true, data: {} }
      }

      const result = await updateSoftwareSetup(
        {
          id: softwareSetup.id,
          renewalDate: formData.renewalDate || null,
          licenseType: formData.licenseType || null,
          vendorId: formData.vendor?.id || null,
          contract: formData.currentFile,
        },
        companyId
      )

      const handled = handleResult(result, (formErrors) => {
        form.setErrors({ ...formErrors })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }
      return handled
    },
  })

  if (!softwareSetup) {
    return
  }

  const addFile = (files: File[]): void => {
    const file = files[0] || undefined
    form.setValues({ ...form.values, currentFile: file })
  }
  const removeFile = () => {
    form.setValues({ ...form.values, currentFile: undefined })
  }
  const { renewalDate, licenseType, currentFile } = form.values

  const onVendorSelect = (vendor: ContactFragment | null) => {
    form.setValues({
      ...form.values,
      vendor: vendor || undefined,
    })
  }

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      open
      onClose={onClose}
      headerBorder
      locked
      closeLabel={i18n.t('common.close')}
      title={
        <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow>
          <Text.Normal weight="semibold">{softwareSetup.software?.name}</Text.Normal>
        </Box>
      }
    >
      {!loading && !!softwareSetup && (
        <form onSubmit={form.handleSubmit}>
          <Box width="full" gap="s24">
            <Text.Header2 color="grey1000">
              {i18n.t('finance.vendors.vendor_detail.title')}
            </Text.Header2>

            {i18n.t('finance.contacts.contact_details.software.description')}

            <FinanceContactsSelector
              label={i18n.t('finance.settings.accounting.vendor_title')}
              onSelect={onVendorSelect}
              value={form.values.vendor}
              disableCreate
              type="finance_contact"
              queryParams={{
                companyId: companyId,
              }}
              variant={'gamma'}
            />

            <Text.Header2 color="grey1000">
              {i18n.t('software_management.software_setups.details.title')}
            </Text.Header2>

            <TextField
              readonly
              label={i18n.t('software_management.software_setups.details.fields.app_name')}
              value={softwareSetup.software?.name}
            />
            <DayPicker
              placeholder="--"
              label={i18n.t('software_management.software_setups.details.fields.renewal_date')}
              value={renewalDate ? dateBackendToUI(renewalDate) : ''}
              onChange={(value) =>
                form.setValues({ ...form.values, renewalDate: dateUIToBackend(value) })
              }
              minDate={moment().startOf('day')}
              error={form.errors.renewalDate}
            />
            <SelectField
              none={i18n.t('common.none')}
              label={i18n.t(
                'software_management.software_setups.details.fields.license_type.label'
              )}
              options={licenseTypeOptions()}
              value={licenseType}
              onChange={(value) => form.setValues({ ...form.values, licenseType: value })}
              error={form.errors.licenseType}
            />

            <Box gap="s8">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('software_management.software_setups.details.fields.contract')}
              </Text>

              {softwareSetup.contract ? (
                <ContractItem
                  contract={softwareSetup.contract}
                  softwareSetupId={softwareSetup.id}
                />
              ) : currentFile ? (
                <FileList
                  files={[currentFile]}
                  onDeleteFile={removeFile}
                  spaced={false}
                  renderIcon={() => (
                    <Box maxHeight="s32" paddingY="s2">
                      <Icon.Large icon="common-file-text-upload" />
                    </Box>
                  )}
                />
              ) : (
                <DropZone
                  accept={acceptedContentTypes(['application/pdf'])}
                  onDrop={addFile}
                  multiple={false}
                />
              )}
            </Box>
            <Box flexDirection="rowReverse">
              <Button disabled={updating} submit>
                {i18n.t('common.update')}
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Sidepanel>
  )
}
